import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BarChartIcon from '@mui/icons-material/BarChart';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { toast } from 'react-toastify';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';
import {
  getSignupCodeStats,
  getSignupCodeTimeseries,
  createSignupCode,
  updateSignupCode,
  deleteSignupCode
} from '../../Api/SignupCodes';
import StandardListView from '../../Components/StandardComponents/StandardListView/StandardListView';
import SignupCodeModal from '../../Components/SignupCodes/SignupCodeModal';
import useD8ListLayout from '../../hooks/useD8ListLayout';
import { isTrinityPortal, trinityProductOrigin } from '../../Utils/adminUserTypes';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend);

const ROUTE_CHIP_STYLES = {
  simplified: { label: 'Simplified', backgroundColor: '#ECFDF5', color: '#047857' },
  brand: { label: 'Brand', backgroundColor: '#EEF2FF', color: '#4338CA' },
  full: { label: 'Full', backgroundColor: '#FFF7ED', color: '#C2410C' }
};

const signupLinkFor = (code) => {
  const origin = isTrinityPortal()
    ? trinityProductOrigin()
    : window.location.origin;
  return `${origin}/register/signup/${code}`;
};

const codeToPayload = (item = {}) => ({
  code: item.code,
  display_name: item.display_name,
  description: item.description || null,
  onboarding_route: item.onboarding_route,
  welcome_message: item.welcome_message || null,
  logo_url: item.logo_url || null,
  default_country: item.default_country || null,
  allowed_industries: item.allowed_industries || null,
  is_active: item.is_active
});

const formatAllowedIndustries = (row) => {
  const industries = Array.isArray(row?.allowed_industries)
    ? row.allowed_industries
    : [];
  if (industries.length === 0) return 'All';
  if (industries.length === 1) return industries[0];
  return `${industries[0]}, +${industries.length - 1} more`;
};

/* Per-code funnel detail: stage bars, step drop-off table and daily trend chart */
const SignupCodeDetailDialog = ({ open, onClose, row }) => {
  const [series, setSeries] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(false);

  useEffect(() => {
    if (!open || !row?.id) return;
    let cancelled = false;
    const loadSeries = async () => {
      try {
        setSeriesLoading(true);
        const response = await getSignupCodeTimeseries(row.id);
        if (!cancelled) setSeries(response?.data || []);
      } catch (err) {
        if (!cancelled) setSeries([]);
      } finally {
        if (!cancelled) setSeriesLoading(false);
      }
    };
    loadSeries();
    return () => { cancelled = true; };
  }, [open, row?.id]);

  if (!row) return null;

  const funnelStages = [
    { label: 'Clicks', value: row.clicks },
    { label: 'OTP Sent', value: row.otp_sent },
    { label: 'OTP Verified', value: row.otp_verified },
    { label: 'Registered', value: row.registrations },
    { label: 'Onboarding Started', value: row.onboarding_started },
    { label: 'Completed', value: row.onboarding_completed }
  ];
  const maxStage = Math.max(1, ...funnelStages.map((stage) => stage.value || 0));

  const chartData = {
    labels: series.map((bucket) => bucket.date),
    datasets: [
      {
        label: 'Clicks',
        data: series.map((bucket) => bucket.clicks),
        borderColor: '#1D3DFC',
        backgroundColor: 'rgba(29, 61, 252, 0.15)',
        tension: 0.3
      },
      {
        label: 'Registrations',
        data: series.map((bucket) => bucket.registrations),
        borderColor: '#F59E0B',
        backgroundColor: 'rgba(245, 158, 11, 0.15)',
        tension: 0.3
      },
      {
        label: 'Completions',
        data: series.map((bucket) => bucket.completions),
        borderColor: '#00CC85',
        backgroundColor: 'rgba(0, 204, 133, 0.15)',
        tension: 0.3
      }
    ]
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {row.display_name || row.code}
          <Chip size="small" label={row.code} variant="outlined" />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Funnel</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          {funnelStages.map((stage) => (
            <Box key={stage.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ width: 140, color: '#6B7280' }}>
                {stage.label}
              </Typography>
              <Box sx={{ flex: 1, backgroundColor: '#F3F4F6', borderRadius: '4px', height: 18 }}>
                <Box
                  sx={{
                    width: `${Math.round(((stage.value || 0) / maxStage) * 100)}%`,
                    minWidth: stage.value ? '2%' : 0,
                    backgroundColor: '#1D3DFC',
                    borderRadius: '4px',
                    height: '100%'
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ width: 48, textAlign: 'right', fontWeight: 600 }}>
                {stage.value || 0}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Last 30 days
          {seriesLoading && <CircularProgress size={14} sx={{ ml: 1 }} />}
        </Typography>
        <Box sx={{ height: 260, mb: 3 }}>
          {series.length > 0 ? (
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom' } },
                scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ color: '#6B7280', pt: 4, textAlign: 'center' }}>
              {seriesLoading ? 'Loading trend…' : 'No activity in the last 30 days.'}
            </Typography>
          )}
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Step drop-off (started, not yet completed)
        </Typography>
        {(row.step_breakdown || []).length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Flow</TableCell>
                <TableCell>Current Step</TableCell>
                <TableCell align="right">Organizations</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {row.step_breakdown.map((step) => (
                <TableRow key={`${step.flow}-${step.step}`}>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{step.flow || 'unknown'}</TableCell>
                  <TableCell>{step.step}</TableCell>
                  <TableCell align="right">{step.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            No organizations currently mid-onboarding for this code.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const SignupCodesQuickFilters = ({ from, to, onFromChange, onToChange, loading }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      alignItems: { xs: 'flex-start', md: 'center' },
      gap: { xs: 2, md: 3 },
      width: '100%',
      py: 1,
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      mb: 2,
      px: 2
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#111827', fontSize: '15px' }}>
      Date Range:
    </Typography>
    <TextField
      size="small"
      type="date"
      label="From"
      value={from}
      onChange={(e) => onFromChange(e.target.value)}
      InputLabelProps={{ shrink: true }}
    />
    <TextField
      size="small"
      type="date"
      label="To"
      value={to}
      onChange={(e) => onToChange(e.target.value)}
      InputLabelProps={{ shrink: true }}
    />
    <Typography variant="caption" sx={{ color: '#6B7280' }}>
      Leave empty for all-time numbers.
    </Typography>
    {loading && <CircularProgress size={20} />}
  </Box>
);

const SignupCodesPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({ open: false, editing: null, saving: false });
  const [detailRow, setDetailRow] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const { pageShellSx, listFlexSx, listViewProps } = useD8ListLayout();

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getSignupCodeStats({ from: fromDate, to: toDate });
      setRows(response?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load signup codes');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const closeModal = () => setModalState({ open: false, editing: null, saving: false });

  const handleCreateClick = useCallback(() => {
    setModalState({ open: true, editing: null, saving: false });
  }, []);

  const handleEditClick = useCallback((row) => {
    // Stats rows only carry funnel fields; keep the full config fields we know
    setModalState({ open: true, editing: row, saving: false });
  }, []);

  const handleCopyLink = useCallback((row) => {
    const link = signupLinkFor(row.code);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(
        () => toast.success(`Link copied: ${link}`),
        () => toast.error('Failed to copy link')
      );
    } else {
      toast.info(link);
    }
  }, []);

  const handleToggleActive = useCallback(
    async (row) => {
      try {
        await updateSignupCode(row.id, { ...codeToPayload(row), is_active: !row.is_active });
        toast.success(row.is_active ? `Code ${row.code} deactivated` : `Code ${row.code} activated`);
        loadStats();
      } catch (err) {
        toast.error(err?.response?.data?.error || 'Failed to update signup code');
      }
    },
    [loadStats]
  );

  const handleDelete = useCallback(
    async (row) => {
      if (!window.confirm(`Delete signup code "${row.code}"? Its link will stop resolving.`)) {
        return;
      }
      try {
        await deleteSignupCode(row.id);
        toast.success('Signup code deleted');
        loadStats();
      } catch (err) {
        toast.error(err?.response?.data?.error || 'Failed to delete signup code');
      }
    },
    [loadStats]
  );

  const handleModalSubmit = async (payload) => {
    try {
      setModalState((prev) => ({ ...prev, saving: true }));
      if (modalState.editing) {
        await updateSignupCode(modalState.editing.id, payload);
        toast.success('Signup code updated');
      } else {
        await createSignupCode(payload);
        toast.success('Signup code created');
      }
      closeModal();
      loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save signup code');
      setModalState((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setPage(0);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setPage(0);
  }, []);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    const value = event.target.value;
    setRowsPerPage(value === 'all' ? -1 : parseInt(value, 10));
    setPage(0);
  }, []);

  const searchedData = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter(
      (row) =>
        row.code?.toLowerCase().includes(q) ||
        row.display_name?.toLowerCase().includes(q) ||
        row.onboarding_route?.toLowerCase().includes(q)
    );
  }, [rows, searchQuery]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = rowsPerPage === -1 ? searchedData.length : start + rowsPerPage;
    return searchedData.slice(start, end);
  }, [searchedData, page, rowsPerPage]);

  const columns = useMemo(
    () => [
      {
        field: 'code',
        label: 'Code',
        minWidth: 200,
        sortable: true,
        render: (row) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', opacity: row.is_active ? 1 : 0.6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row.code}
              </Typography>
              {!row.is_active && (
                <Chip size="small" label="Inactive" variant="outlined" sx={{ ml: 0.5 }} />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              {row.display_name}
            </Typography>
          </Box>
        )
      },
      {
        field: 'onboarding_route',
        label: 'Route',
        minWidth: 120,
        sortable: true,
        render: (row) => {
          const style = ROUTE_CHIP_STYLES[row.onboarding_route] || ROUTE_CHIP_STYLES.simplified;
          return (
            <Chip
              size="small"
              label={style.label}
              sx={{ backgroundColor: style.backgroundColor, color: style.color }}
            />
          );
        }
      },
      {
        field: 'allowed_industries',
        label: 'Allowed Industries',
        minWidth: 180,
        sortable: false,
        render: (row) => (
          <Typography variant="body2" sx={{ color: '#374151' }}>
            {formatAllowedIndustries(row)}
          </Typography>
        )
      },
      { field: 'clicks', label: 'Clicks', minWidth: 80, align: 'right', sortable: true },
      { field: 'otp_verified', label: 'OTP Verified', minWidth: 110, align: 'right', sortable: true },
      { field: 'registrations', label: 'Registered', minWidth: 100, align: 'right', sortable: true },
      { field: 'onboarding_started', label: 'Started', minWidth: 90, align: 'right', sortable: true },
      { field: 'onboarding_completed', label: 'Completed', minWidth: 100, align: 'right', sortable: true },
      {
        field: 'created_at',
        label: 'Created',
        minWidth: 110,
        sortable: true,
        render: (row) => (
          <Typography variant="body2">
            {row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}
          </Typography>
        )
      }
    ],
    []
  );

  const actions = useMemo(
    () => [
      {
        label: 'View Stats',
        icon: <BarChartIcon />,
        onClick: (row) => setDetailRow(row)
      },
      {
        label: 'Copy Link',
        icon: <ContentCopyIcon />,
        onClick: (row) => handleCopyLink(row)
      },
      {
        label: 'Edit',
        icon: <EditIcon />,
        onClick: (row) => handleEditClick(row)
      },
      {
        label: 'Toggle Active',
        icon: <ToggleOnIcon />,
        onClick: (row) => handleToggleActive(row)
      },
      {
        label: 'Delete',
        icon: <DeleteIcon />,
        onClick: (row) => handleDelete(row),
        color: 'error'
      }
    ],
    [handleCopyLink, handleDelete, handleEditClick, handleToggleActive]
  );

  return (
    <Box sx={pageShellSx}>
      <Box sx={listFlexSx}>
        <StandardListView
          {...listViewProps}
          title="Signup Codes"
          subtitle="Manage partner/marketing signup links (/register/signup/<code>) and track their funnel: clicks → registrations → onboarding completed."
          columns={columns}

          rows={paginatedData}
          totalRows={searchedData.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          onSearch={handleSearch}
          onClearSearch={handleClearSearch}
          searchQuery={searchQuery}
          actions={actions}
          showMoreIcon
          loading={loading}
          addButtonLabel="Add Code"
          onAddButtonClick={handleCreateClick}
          isEdit
          onRowClick={(row) => setDetailRow(row)}
          quickFilterComponent={
            <SignupCodesQuickFilters
              from={fromDate}
              to={toDate}
              onFromChange={setFromDate}
              onToChange={setToDate}
              loading={loading}
            />
          }
        />
      </Box>

      <SignupCodeModal
        open={modalState.open}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        initialData={modalState.editing}
        isSubmitting={modalState.saving}
      />

      <SignupCodeDetailDialog
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        row={detailRow}
      />
    </Box>
  );
};

export default SignupCodesPage;
